import { useState, useCallback } from 'react';
import type { TourStep } from '@/components/ui/product-tour';

const TOUR_STORAGE_KEY = 'create-audit-tour-completed';

export function useCreateAuditTour() {
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [hasCompletedTour, setHasCompletedTour] = useState(() => {
    return localStorage.getItem(TOUR_STORAGE_KEY) === 'true';
  });

  const startTour = useCallback(() => {
    setIsTourOpen(true);
  }, []);

  const closeTour = useCallback(() => {
    setIsTourOpen(false);
  }, []);

  const completeTour = useCallback(() => {
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    setHasCompletedTour(true);
    setIsTourOpen(false);
  }, []);

  const resetTour = useCallback(() => {
    localStorage.removeItem(TOUR_STORAGE_KEY);
    setHasCompletedTour(false);
  }, []);

  const tourSteps: TourStep[] = [
    {
      target: '[data-tour="audit-wizard-header"]',
      title: 'Welcome to Audit Creation',
      content: 'This wizard will guide you through creating a comprehensive audit. Follow the steps to define audit details, scope, schedule, and team assignments.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="audit-steps-sidebar"]',
      title: 'Audit Creation Steps',
      content: 'Track your progress through the 5-step audit creation process. You can navigate back to previous steps at any time to make changes.',
      placement: 'right',
    },
    {
      target: '[data-tour="audit-save-draft"]',
      title: 'Save Your Progress',
      content: 'Save your work as a draft at any time. Your progress is also auto-saved, so you can return later to complete the audit creation.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="audit-step-content"]',
      title: 'Step Content',
      content: 'Each step contains specific fields and options. Fill out the required information before proceeding to the next step.',
      placement: 'top',
    },
    {
      target: '[data-tour="audit-navigation"]',
      title: 'Navigation Controls',
      content: 'Use these buttons to move between steps. The "Next" button will be enabled once you complete all required fields in the current step.',
      placement: 'top',
    },
  ];

  return {
    isTourOpen,
    hasCompletedTour,
    tourSteps,
    startTour,
    closeTour,
    completeTour,
    resetTour,
  };
}
